import { useMemo, useState } from "react";
import { Head, Link, router } from "@inertiajs/react";
import { CheckCircle2, Circle, GraduationCap, Search, Trash2 } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import EmptyState from "@/components/EmptyState";
import { DataTable, Th, Td, rowClass, EmptyRow } from "@/components/shared/DataTable";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatMoney, formatNumber, pluralize } from "@/lib/format";

export default function Index({ school, membership, campaign, students = [], remove_path }) {
  const confirm = useConfirm();
  const [query, setQuery] = useState("");
  const [classroom, setClassroom] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const classrooms = useMemo(() => [...new Set(students.map((s) => s.classroom_name))], [students]);
  const needle = query.trim().toLowerCase();
  const shown = students.filter((s) =>
    (!classroom || s.classroom_name === classroom) &&
    (!needle || s.full_name.toLowerCase().includes(needle) || s.claim_code.toLowerCase().includes(needle))
  );
  const activated = students.filter((s) => s.claimed).length;
  // Only what's on screen counts, so a search or filter never hides a pick.
  const picked = shown.filter((s) => selected.has(s.id));
  const pickable = shown.filter((s) => s.removable);
  const allPicked = pickable.length > 0 && picked.length === pickable.length;
  const columns = remove_path ? 7 : 6;

  const toggle = (id) => setSelected((current) => {
    const next = new Set(current);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allPicked ? new Set() : new Set(pickable.map((s) => s.id)));
  const onRemove = async () => {
    const ok = await confirm({
      title: picked.length === 1 ? `Remove ${picked[0].full_name}?` : `Remove ${pluralize(picked.length, "student")}?`,
      body: "Their reading pages, logged minutes, and family activations are deleted. This can't be undone.",
      confirmLabel: picked.length === 1 ? `Remove ${picked[0].first_name}` : `Remove ${pluralize(picked.length, "student")}`,
      destructive: true,
    });
    if (ok) {
      router.post(remove_path, { student_ids: picked.map((s) => s.id) }, {
        preserveScroll: true,
        onSuccess: () => setSelected(new Set()),
      });
    }
  };

  return (
    <SchoolLayout school={school} membership={membership} eyebrow={campaign?.name} title="Students">
      <Head title={`Students · ${school.name}`} />

      {students.length === 0 ? (
        <EmptyState icon={GraduationCap} title="No students yet" description="Students appear here once their classrooms are imported." />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {picked.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-foreground">{formatNumber(picked.length)} selected</p>
                <Button variant="destructive" size="sm" onClick={onRemove}><Trash2 className="h-4 w-4" /> Remove</Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {pluralize(students.length, "student")} · {formatNumber(activated)} activated
              </p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <label htmlFor="student-search" className="sr-only">Search students</label>
                <Input
                  id="student-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Name or code"
                  className="h-9 pl-9 text-sm sm:w-56"
                />
              </div>
              {classrooms.length > 1 && (
                <>
                  <label htmlFor="student-classroom" className="sr-only">Classroom</label>
                  <Select
                    id="student-classroom"
                    size="sm"
                    value={classroom}
                    onChange={(e) => setClassroom(e.target.value)}
                    placeholder="All classrooms"
                    options={classrooms.map((name) => ({ value: name, label: formatRoom(name) }))}
                    className="sm:w-48"
                  />
                </>
              )}
            </div>
          </div>

          <DataTable>
            <thead>
              <tr>
                {remove_path && (
                  <Th className="w-px pr-0">
                    <Checkbox checked={allPicked} onChange={toggleAll} disabled={pickable.length === 0} aria-label="Select all students shown" />
                  </Th>
                )}
                <Th>Student</Th>
                <Th>Classroom</Th>
                <Th align="center">Activated</Th>
                <Th>Activation code</Th>
                <Th align="right">Raised</Th>
                <Th align="right">Minutes</Th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <EmptyRow colSpan={columns}>No students match.</EmptyRow>
              ) : (
                shown.map((student) => (
                  <tr key={student.id} className={rowClass}>
                    {remove_path && (
                      <Td className="w-px pr-0">
                        <Checkbox
                          checked={selected.has(student.id)}
                          onChange={() => toggle(student.id)}
                          disabled={!student.removable}
                          aria-label={`Select ${student.full_name}`}
                          title={student.removable ? undefined : "Has payment history, so it can't be removed"}
                        />
                      </Td>
                    )}
                    <Td>
                      <Link href={student.student_path} className="font-medium text-foreground hover:text-primary">{student.full_name}</Link>
                    </Td>
                    <Td className="text-muted-foreground">
                      {formatRoom(student.classroom_name)}
                      {student.grade && <span> · {formatGrade(student.grade)}</span>}
                    </Td>
                    <Td align="center">
                      {student.claimed
                        ? <CheckCircle2 className="inline h-4 w-4 text-success" aria-label="Activated" />
                        : <Circle className="inline h-4 w-4 text-muted-foreground/40" aria-label="Not activated yet" />}
                    </Td>
                    <Td><code className="rounded bg-secondary/60 px-2 py-1 font-mono text-xs">{student.claim_code}</code></Td>
                    <Td align="right" className="text-foreground">{formatMoney(student.raised_cents)}</Td>
                    <Td align="right" className="text-muted-foreground">{formatNumber(student.minutes_logged)}</Td>
                  </tr>
                ))
              )}
            </tbody>
          </DataTable>
        </div>
      )}
    </SchoolLayout>
  );
}
